import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { type RootState } from '@/store';
import { GenerateChatTitleResponse, GetChatResponse } from '@/api/chat';
import { CreateChatResponse } from '@/api/chat/createChat.ts';
import { FileStatus } from '@/types/file';
import type { ChatSendMessageType, MessageAttachedDocument } from '@/types/chat';
import { draftChat } from '@/constants';

// An improved prompt variant: the original prompt, the improved one, and when it was updated.
export interface ImprovedPromptEntry {
  original: string;
  improved: string;
  updatedAt: string;
  isImproving: boolean;
  isFinished: boolean;
}

export interface ChatDocument {
  documentId: string;
  documentName: string;
  status: FileStatus;
  error?: string;
  /** ISO or locale date string from API when the document was uploaded */
  uploadedAt?: string;
  sizeMb?: number;
  messageId?: string;
  /** When false, the document is sent with the run but not shown as an attachment (e.g. auto-added context). */
  isPromptAttachment?: boolean;
  /** When false, the BE can't preview this document. The Sources panel greys it out and clicks show a toast. */
  isDisplayable?: boolean;
  /**
   * A draft generated in the chat. `documentId` is a `draftVersionId`, so use
   * `/chat/draft/get-version` and `/chat/draft/download-version` instead of `/document/*`.
   */
  isDraft?: boolean;
}
/** A `/chat/documents` list, tagged with the chat it was fetched for. */
export interface ChatDocumentsList {
  chatId: string | undefined;
  documents: ChatDocument[];
}
export interface ChatDocumentDownload {
  downloadPath: string;
  downloadToken: string;
  chatDocumentId: string;
}

interface PendingMessageEntry {
  userPrompt: string;
  createdAt: string;
  isStreaming: boolean;
  attachedDocuments?: MessageAttachedDocument[];
}

interface DeferredSendEntry {
  prompt: string;
  createdAt: string;
  documentIds: string[];
  /**
   * For new-chat deferred sends (no persisted chatId yet), background worker should create a chat first.
   */
  createChat?: boolean;
  /** Optional matter/case id for chat creation context. */
  caseId?: string;
  selectedFileIds?: string[];
  /** Matter history rows picked to prioritise, replayed with the deferred send. */
  selectedHistoryRowIds?: string[];
  existingDocumentIds?: string[];
  messageAttachmentDocuments?: MessageAttachedDocument[];
  messageType?: ChatSendMessageType;
}

interface DeferredSendErrorEntry {
  prompt: string;
  createdAt: string;
  reason?: string;
}

// Keep a stable empty array reference for selectors to avoid
// useSelector warnings about returning new references on each call.
const EMPTY_IMPROVED_PROMPTS: ImprovedPromptEntry[] = [];

interface Chat {
  chatData: GetChatResponse | null;
  documents: ChatDocument[];
  /**
   * Everything `/chat/documents` reports for the chat, drafts included. Display-only —
   * the send endpoint 404s on draft ids, so runs keep being built from `documents`.
   */
  allDocuments: ChatDocument[];
  sessionDocuments: ChatDocument[];
  documentsDownload: Record<string, ChatDocumentDownload>;
  chatId: string | undefined;
  /** Chat id created in the background (e.g. deferred send) that the UI should put into the URL. */
  pendingUrlSyncChatId?: string;
  /** Set when starting a new chat, so the old `:chatId` route param isn't synced back into the store. */
  ignoreSharedLinkChatIdSync: boolean;
  settings: {
    chatDocumentPanel: Record<string, ChatDocumentPanelSettings>;
    /**
     * Matter history row shown in the side panel. Separate from `chatDocumentPanel` because a row
     * has no document id. Only one of the two panels is open at a time.
     */
    chatHistoryRowPanel: ChatHistoryRowPanelSettings | null;
  };
  /** Improved prompts per chat, kept in memory so they survive navigation. */
  improvedPrompts: Record<string, ImprovedPromptEntry[]>;
  /** Last in-flight user message per chat, restored when the user comes back while it's still streaming. */
  pendingMessages: Record<string, PendingMessageEntry | undefined>;
  /**
   * Background "send when documents are ready" queue.
   * Keyed by chatId. Used to continue polling + send even when ChatPage unmounts.
   */
  deferredSends: Record<string, DeferredSendEntry | undefined>;
  /**
   * Short-lived marker for when a background deferred send fails.
   * Used to restore the user's original prompt back into the input box.
   */
  deferredSendErrors: Record<string, DeferredSendErrorEntry | undefined>;
  /** Unsent prompts per chat box, keyed by `getUnsentPromptKey`. Memory only. `clearChat` keeps them. */
  unsentPrompts: Record<string, string>;
  lastResponseAt: number | null;
  /** Documents whose preview failed as unsupported. Shows the warning and skips re-requesting. */
  unviewableDocumentIds: Record<string, true>;
  /** Clockwise turn of each image preview in degrees, kept until the chat changes. */
  imagePreviewRotations: Record<string, number>;
}

export interface ChatDocumentPanelSettings {
  open: boolean;
  documentId: string;
  pageNumber: number;
  highlightText?: string;
  createdByAi?: boolean;
  highlightTrigger?: number;
  /** Plain-text citation extract in the side panel instead of the whole document */
  citationExtractPreview?: boolean;
  /** Header title when `citationExtractPreview` is true */
  citationPanelTitle?: string;
}

export interface ChatHistoryRowPanelSettings {
  historyRowId: string;
  /** The row's matter — `/matter/history-row-details` is scoped to it. */
  matterId: string;
}

const initialState: Chat = {
  chatData: null,
  documents: [],
  allDocuments: [],
  sessionDocuments: [],
  documentsDownload: {},
  // Store per-tab so opening a new browser window does not reuse an old chat.
  chatId: typeof window !== 'undefined' ? window.sessionStorage.getItem('chatId') || undefined : undefined,
  pendingUrlSyncChatId: undefined,
  ignoreSharedLinkChatIdSync: false,
  settings: {
    chatDocumentPanel: {},
    chatHistoryRowPanel: null,
  },
  improvedPrompts: {},
  pendingMessages: {},
  deferredSends: {},
  deferredSendErrors: {},
  unsentPrompts: {},
  lastResponseAt: null,
  unviewableDocumentIds: {},
  imagePreviewRotations: {},
};

const chat = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setChatData: (state, action: PayloadAction<GetChatResponse | null>) => {
      state.chatData = action.payload;
    },
    setChatId: (state, action: PayloadAction<CreateChatResponse | null>) => {
      const nextChatId = action.payload?.chatId;
      // A chat being created keeps the turns; moving to another chat starts them afresh.
      if (state.chatId && nextChatId !== state.chatId) state.imagePreviewRotations = {};
      state.chatId = nextChatId;
    },
    setPendingUrlSyncChatId: (state, action: PayloadAction<{ chatId: string } | null>) => {
      state.pendingUrlSyncChatId = action.payload?.chatId;
    },
    setChatTitle: (state, action: PayloadAction<GenerateChatTitleResponse | null>) => {
      if (!state.chatData) return;
      state.chatData.chatTitle = action.payload?.chatTitle || '';
    },
    // A list that lands after a chat switch belongs to the chat left; sending its ids 400s.
    setDocumentsListData: (state, action: PayloadAction<ChatDocumentsList>) => {
      if (action.payload.chatId !== state.chatId) return;
      state.documents = action.payload.documents;
    },
    setAllDocumentsListData: (state, action: PayloadAction<ChatDocumentsList>) => {
      if (action.payload.chatId !== state.chatId) return;
      state.allDocuments = action.payload.documents;
    },
    addChatDocument: (
      state,
      action: PayloadAction<{
        documentId: string;
        documentName: string;
        isPromptAttachment?: boolean;
        /** The chat the upload was created in; it's dropped if another chat is open by now. */
        chatId?: string;
      }>,
    ) => {
      const { documentId, documentName, isPromptAttachment, chatId } = action.payload;
      if (chatId !== undefined && chatId !== state.chatId) return;
      // The `/chat/documents` refetch may have already added this document — don't add it twice.
      if (state.sessionDocuments.some((d) => d.documentId === documentId)) return;
      state.sessionDocuments.push({
        documentId,
        documentName,
        status: FileStatus.UPLOADING,
        ...(isPromptAttachment === false ? { isPromptAttachment: false } : {}),
      });
    },
    updateChatDocumentStatus: (
      state,
      action: PayloadAction<{ documentId: string; status: ChatDocument['status']; error?: string }>,
    ) => {
      const doc = state.documents.find((d) => d.documentId === action.payload.documentId);
      if (doc) {
        doc.status = action.payload.status;
        doc.error = action.payload.error;
      }
      const sessionDoc = state.sessionDocuments.find((d) => d.documentId === action.payload.documentId);
      if (sessionDoc) {
        sessionDoc.status = action.payload.status;
        sessionDoc.error = action.payload.error;
      }
    },
    clearDocuments: (state) => {
      state.documents = [];
      state.allDocuments = [];
    },
    clearSessionDocuments: (state) => {
      state.sessionDocuments = [];
    },
    restoreSessionDocuments: (state, action: PayloadAction<ChatDocument[]>) => {
      for (const doc of action.payload) {
        if (state.sessionDocuments.some((d) => d.documentId === doc.documentId)) continue;
        state.sessionDocuments.push(doc);
      }
    },
    promoteSessionDocuments: (state) => {
      state.sessionDocuments.forEach((doc) => {
        if (!state.documents.some((d) => d.documentId === doc.documentId)) {
          state.documents.push(doc);
        }
      });
      state.sessionDocuments = [];
    },
    removeChatDocument: (state, action: PayloadAction<string | { documentId: string }>) => {
      const documentIdToRemove = typeof action.payload === 'string' ? action.payload : action.payload.documentId;

      if (Array.isArray(state.documents)) {
        state.documents = state.documents.filter((doc) => doc.documentId !== documentIdToRemove);
      }
      state.allDocuments = state.allDocuments.filter((doc) => doc.documentId !== documentIdToRemove);
      state.sessionDocuments = state.sessionDocuments.filter((doc) => doc.documentId !== documentIdToRemove);
    },
    setChatDocumentPanelSettings: (state, action: PayloadAction<ChatDocumentPanelSettings>) => {
      const { documentId, open } = action.payload;
      const { chatDocumentPanel } = state.settings;

      chatDocumentPanel[documentId] = action.payload;

      // If the target document is being opened, close all other documents. There should be only one open document at a time.
      if (!open) {
        return;
      }

      state.settings.chatHistoryRowPanel = null;

      for (const id in chatDocumentPanel) {
        if (id !== documentId) {
          chatDocumentPanel[id].open = false;
        }
      }
    },
    /** Opens the history-row details panel, closing whichever document panel was open. */
    openChatHistoryRowPanel: (state, action: PayloadAction<ChatHistoryRowPanelSettings>) => {
      state.settings.chatHistoryRowPanel = action.payload;

      for (const id in state.settings.chatDocumentPanel) {
        state.settings.chatDocumentPanel[id].open = false;
      }
    },
    closeChatHistoryRowPanel: (state) => {
      state.settings.chatHistoryRowPanel = null;
    },
    markDocumentUnviewable: (state, action: PayloadAction<{ documentId: string }>) => {
      state.unviewableDocumentIds[action.payload.documentId] = true;
    },
    /** Turns an image preview a further 90°, clockwise unless `anticlockwise` is set. */
    rotateImagePreview: (state, action: PayloadAction<{ documentId: string; anticlockwise?: boolean }>) => {
      const { documentId, anticlockwise } = action.payload;
      const turn = anticlockwise ? 270 : 90;
      state.imagePreviewRotations[documentId] = ((state.imagePreviewRotations[documentId] ?? 0) + turn) % 360;
    },
    addChatDocumentDownload: (state, action: PayloadAction<ChatDocumentDownload>) => {
      if (!state.documentsDownload[action.payload.chatDocumentId]) {
        state.documentsDownload[action.payload.chatDocumentId] = action.payload;
      }
    },
    setLastResponseAt: (state) => {
      state.lastResponseAt = Date.now();
    },
    clearChat: (state) => {
      state.chatData = null;
      state.documents = [];
      state.allDocuments = [];
      state.sessionDocuments = [];
      state.documentsDownload = {};
      state.chatId = undefined;
      state.settings.chatDocumentPanel = {};
      state.settings.chatHistoryRowPanel = null;
      state.improvedPrompts = {};
      // A chat's send carries on in the background (chatListener); only the draft's belongs to this page.
      delete state.pendingMessages[draftChat];
      delete state.deferredSends[draftChat];
      delete state.deferredSendErrors[draftChat];
      state.lastResponseAt = null;
      state.unviewableDocumentIds = {};
      state.imagePreviewRotations = {};
    },
    clearChatButKeepId: (state) => {
      state.chatData = null;
      state.documents = [];
      state.allDocuments = [];
      state.sessionDocuments = [];
      state.documentsDownload = {};
      state.settings.chatDocumentPanel = {};
      state.settings.chatHistoryRowPanel = null;
      state.imagePreviewRotations = {};
    },
    setIgnoreSharedLinkChatIdSync: (state, action: PayloadAction<boolean>) => {
      state.ignoreSharedLinkChatIdSync = action.payload;
    },
    setImprovedPromptForChat: (
      state,
      action: PayloadAction<{
        chatId: string;
        original: string;
        improved: string;
        isImproving: boolean;
        isFinished: boolean;
      }>,
    ) => {
      const { chatId, original, improved, isImproving, isFinished } = action.payload;
      if (!chatId) {
        return;
      }
      const existing = state.improvedPrompts[chatId] ?? [];
      const updatedEntry: ImprovedPromptEntry = {
        original,
        improved,
        updatedAt: new Date().toISOString(),
        isImproving,
        isFinished,
      };
      const idx = existing.findIndex((entry) => entry.original === original);
      // If we already improved this original prompt, overwrite it; otherwise append a new entry.
      if (idx >= 0) {
        existing[idx] = updatedEntry;
      } else {
        existing.push(updatedEntry);
      }
      state.improvedPrompts[chatId] = existing;
    },
    clearImprovedPromptForChat: (state, action: PayloadAction<{ chatId: string; original: string }>) => {
      const { chatId, original } = action.payload;
      const list = state.improvedPrompts[chatId];
      if (!list) return;
      state.improvedPrompts[chatId] = list.filter((entry) => entry.original !== original);
      if (state.improvedPrompts[chatId].length === 0) {
        delete state.improvedPrompts[chatId];
      }
    },
    setPendingMessageForChat: (
      state,
      action: PayloadAction<{
        chatId: string;
        userPrompt: string;
        isStreaming?: boolean;
        attachedDocuments?: MessageAttachedDocument[];
      }>,
    ) => {
      const { chatId, userPrompt } = action.payload;
      if (!chatId) return;
      state.pendingMessages[chatId] = {
        userPrompt,
        createdAt: new Date().toISOString(),
        isStreaming: action.payload.isStreaming ?? true,
        attachedDocuments: action.payload.attachedDocuments,
      };
    },
    clearPendingMessageForChat: (state, action: PayloadAction<{ chatId: string }>) => {
      const { chatId } = action.payload;
      if (!chatId) return;
      delete state.pendingMessages[chatId];
    },

    setDeferredSendForChat: (
      state,
      action: PayloadAction<{
        chatId: string;
        prompt: string;
        documentIds: string[];
        createChat?: boolean;
        caseId?: string;
        selectedFileIds?: string[];
        selectedHistoryRowIds?: string[];
        existingDocumentIds?: string[];
        messageAttachmentDocuments?: MessageAttachedDocument[];
        messageType?: ChatSendMessageType;
      }>,
    ) => {
      const { chatId, prompt, documentIds } = action.payload;
      if (!chatId) return;
      state.deferredSends[chatId] = {
        prompt,
        documentIds,
        createChat: action.payload.createChat,
        caseId: action.payload.caseId,
        selectedFileIds: action.payload.selectedFileIds,
        selectedHistoryRowIds: action.payload.selectedHistoryRowIds,
        existingDocumentIds: action.payload.existingDocumentIds,
        messageAttachmentDocuments: action.payload.messageAttachmentDocuments,
        messageType: action.payload.messageType,
        createdAt: new Date().toISOString(),
      };
    },

    clearDeferredSendForChat: (state, action: PayloadAction<{ chatId: string }>) => {
      const { chatId } = action.payload;
      if (!chatId) return;
      delete state.deferredSends[chatId];
    },

    setDeferredSendErrorForChat: (
      state,
      action: PayloadAction<{
        chatId: string;
        prompt: string;
        reason?: string;
      }>,
    ) => {
      const { chatId, prompt, reason } = action.payload;
      if (!chatId) return;
      state.deferredSendErrors[chatId] = {
        prompt,
        reason,
        createdAt: new Date().toISOString(),
      };
    },

    clearDeferredSendErrorForChat: (state, action: PayloadAction<{ chatId: string }>) => {
      const { chatId } = action.payload;
      if (!chatId) return;
      delete state.deferredSendErrors[chatId];
    },

    /** A blank prompt doesn't clear the entry — see `useUnsentPrompt`. */
    setUnsentPrompt: (state, action: PayloadAction<{ key: string; prompt: string }>) => {
      const { key, prompt } = action.payload;
      if (prompt.trim()) {
        state.unsentPrompts[key] = prompt;
      }
    },

    clearUnsentPrompt: (state, action: PayloadAction<{ key: string }>) => {
      delete state.unsentPrompts[action.payload.key];
    },

    /**
     * Moves draft-keyed state to the new chat id, so stale prompts/attachments aren't restored and
     * no second chat is created.
     */
    migrateDraftChatState: (state, action: PayloadAction<{ chatId: string }>) => {
      const nextChatId = action.payload.chatId;
      if (!nextChatId) return;
      if (nextChatId === draftChat) return;

      const draftPending = state.pendingMessages[draftChat];
      if (draftPending && !state.pendingMessages[nextChatId]) {
        state.pendingMessages[nextChatId] = draftPending;
      }
      delete state.pendingMessages[draftChat];

      const draftDeferred = state.deferredSends[draftChat];
      if (draftDeferred && !state.deferredSends[nextChatId]) {
        state.deferredSends[nextChatId] = draftDeferred;
      }
      delete state.deferredSends[draftChat];

      const draftErr = state.deferredSendErrors[draftChat];
      if (draftErr && !state.deferredSendErrors[nextChatId]) {
        state.deferredSendErrors[nextChatId] = draftErr;
      }
      delete state.deferredSendErrors[draftChat];

      const draftImproved = state.improvedPrompts[draftChat];
      if (draftImproved && !state.improvedPrompts[nextChatId]) {
        state.improvedPrompts[nextChatId] = draftImproved;
      }
      delete state.improvedPrompts[draftChat];
    },
  },
});

export const {
  setChatData,
  setDocumentsListData,
  setAllDocumentsListData,
  addChatDocument,
  updateChatDocumentStatus,
  removeChatDocument,
  setChatId,
  setPendingUrlSyncChatId,
  setChatTitle,
  clearDocuments,
  setChatDocumentPanelSettings,
  openChatHistoryRowPanel,
  closeChatHistoryRowPanel,
  markDocumentUnviewable,
  rotateImagePreview,
  addChatDocumentDownload,
  clearChat,
  clearSessionDocuments,
  restoreSessionDocuments,
  promoteSessionDocuments,
  setImprovedPromptForChat,
  clearImprovedPromptForChat,
  setPendingMessageForChat,
  clearPendingMessageForChat,
  setDeferredSendForChat,
  clearDeferredSendForChat,
  setDeferredSendErrorForChat,
  clearDeferredSendErrorForChat,
  setUnsentPrompt,
  clearUnsentPrompt,
  migrateDraftChatState,
  clearChatButKeepId,
  setIgnoreSharedLinkChatIdSync,
  setLastResponseAt,
} = chat.actions;

export const selectChatId = (state: RootState) => {
  return state.chat.chatId;
};

export const selectPendingUrlSyncChatId = (state: RootState) => state.chat.pendingUrlSyncChatId;

export const selectIgnoreSharedLinkChatIdSync = (state: RootState) => {
  return state.chat.ignoreSharedLinkChatIdSync;
};

export const selectChatTitle = (state: RootState) => {
  return state.chat.chatData?.chatTitle;
};

export const selectChatMatterInfo = (state: RootState) => {
  return state.chat.chatData?.matter;
};

/**
 * Whether the current user created this chat (only the creator can rename and share). `undefined`
 * until `GET /chat` loads.
 */
export const selectIsChatCreator = (state: RootState) => {
  return state.chat.chatData?.isCreator;
};

export const selectLastResponseAt = (state: RootState) => {
  return state.chat.lastResponseAt;
};

/** When the last response finished, from the BE. Only meaningful for the last assistant message. */
export const selectChatLastResponse = (state: RootState) => {
  return state.chat.chatData?.lastResponse ?? null;
};

export const selectChatDocumentPanelSettings = (state: RootState) => {
  return state.chat.settings.chatDocumentPanel;
};

export const selectChatHistoryRowPanel = (state: RootState) => {
  return state.chat.settings.chatHistoryRowPanel;
};

export const selectChatDocumentsDownload = (state: RootState) => {
  return state.chat.documentsDownload;
};

export const selectUnviewableDocumentIds = (state: RootState) => {
  return state.chat.unviewableDocumentIds;
};

/** Clockwise turn of an image preview in degrees: 0, 90, 180 or 270. */
export const selectImagePreviewRotation = (state: RootState, documentId: string) => {
  return state.chat.imagePreviewRotations[documentId] ?? 0;
};

// Documents added in current session only (for input list: uploads in progress / just added).
export const selectSessionChatDocuments = (state: RootState): ChatDocument[] => {
  return state.chat.sessionDocuments;
};

export const selectChatDocuments = (state: RootState) => {
  return state.chat.documents;
};

// Every document the chat has, drafts included. Only for listing files (the download
// modal) — never for building a send payload, which must stay on `selectChatDocuments`.
export const selectAllChatDocuments = (state: RootState) => {
  return state.chat.allDocuments;
};

export const selectImprovedPromptsForChat = (state: RootState, chatId: string) => {
  return state.chat.improvedPrompts[chatId] ?? EMPTY_IMPROVED_PROMPTS;
};

export const selectPendingMessageForChat = (state: RootState, chatId: string | undefined) => {
  if (!chatId) return undefined;
  return state.chat.pendingMessages[chatId];
};

export const selectDeferredSendForChat = (state: RootState, chatId: string | undefined) => {
  if (!chatId) return undefined;
  return state.chat.deferredSends[chatId];
};

export const selectDeferredSendErrorForChat = (state: RootState, chatId: string | undefined) => {
  if (!chatId) return undefined;
  return state.chat.deferredSendErrors[chatId];
};

export const selectUnsentPrompt = (state: RootState, key: string | undefined) => {
  if (!key) return undefined;
  return state.chat.unsentPrompts[key];
};

export const chatReducer = chat.reducer;
